<template>
  <page-outline title="Rapprochement manuel des opérations">
    <template slot="pageContent">
      <b-row v-if="error">
        <b-col class="text-center my-5">
          <p class="text-secondary">
            {{ $t('common.error_fetch_data') }}
          </p>
          <p class="text-primary link" href="#" @click.prevent="fetchData">
            {{$t('common.rfrsh_page')}} <f7-icon fa="refresh"></f7-icon>
          </p>
        </b-col>
      </b-row>
      <b-row v-if="!error">
        <b-col></b-col>
        <b-col>
          <b-form-select v-model="selectedAccount" :options="accounts"></b-form-select>
        </b-col>
      </b-row>
      <b-row v-if="!error">
        <!-- Paiements -->
        <b-col>
          <b-card-group deck>
            <b-card no-body header="Paiements en attente">
              <div v-if="!rawData" class="d-flex justify-content-center py-2"><b-spinner class="greenFont"/></div>
              <div v-else-if="dpAll.length === 0 && rtpAll.length === 0" class="d-flex justify-content-center my-3">Pas de paiement en attente</div>
              <b-list-group flush v-else>
                <b-form-checkbox-group v-if="dpAll && dpAll.length > 0" v-model="dpList">
                  <b-list-group-item v-for="operation in dpAll" :key="operation.idOperationStatement" class="d-flex justify-content-between align-middle">
                    <label :for="`dp-${operation.idOperationStatement}`" class="d-flex flex-column">
                      <span>
                        {{operation.remittanceInformation}} | {{operation.personName}} |
                        <b v-if="operation.direction === '1'" class="text-success">+{{operation.amount}}{{currency}}</b>
                        <b v-else class="text-danger">-{{operation.amount}}{{currency}}</b>
                      </span>
                      <small>{{date(operation.date)}}</small>
                    </label>
                    <b-form-checkbox :value="operation.idOperationStatement" :id="`dp-${operation.idOperationStatement}`"></b-form-checkbox>
                  </b-list-group-item>
                </b-form-checkbox-group>
                <b-form-checkbox-group v-if="rtpAll && rtpAll.length > 0" v-model="rtpList">
                  <b-list-group-item v-for="operation in rtpAll" :key="operation.idOperationStatement" class="d-flex justify-content-between align-middle">
                    <label :for="`rtp-${operation.idOperationStatement}`" class="d-flex flex-column">
                      <span>
                        {{operation.remittanceInformation}} | {{operation.personName}} |
                        <b v-if="operation.direction === '1'" class="text-success">+{{operation.amount}}{{currency}}</b>
                        <b v-else class="text-danger">-{{operation.amount}}{{currency}}</b>
                      </span>
                      <small>{{date(operation.date)}}</small>
                    </label>
                    <b-form-checkbox :value="operation.idOperationStatement" :id="`dp-${operation.idOperationStatement}`"></b-form-checkbox>
                  </b-list-group-item>
                </b-form-checkbox-group>
              </b-list-group>
            </b-card>
          </b-card-group>
          <!-- <div v-else class="d-flex justify-content-center mt-3">Pas de paiement en attente</div> -->
        </b-col>
        <!-- Opérations -->
        <b-col>
          <b-card-group deck>
            <b-card no-body header="Opérations bancaires">
              <div v-if="!rawData" class="d-flex justify-content-center py-2"><b-spinner class="greenFont"/></div>
              <div v-else-if="opAll.length === 0" class="d-flex justify-content-center my-3">Pas d'opération à rapprocher</div>
              <b-list-group flush v-else>
                <b-form-checkbox-group v-model="opList">
                  <b-list-group-item v-for="operation in opAll" :key="operation.idOperationStatement" class="d-flex justify-content-start align-middle">
                    <b-form-checkbox :value="operation.idOperationStatement" :id="`op-${operation.idOperationStatement}`"></b-form-checkbox>
                    <label :for="`op-${operation.idOperationStatement}`" class="d-flex flex-column">
                      <span>
                        {{operation.remittanceInformation}} | {{operation.personName}} |
                        <b v-if="operation.direction === '1'" class="text-success">+{{operation.amount}}{{currency}}</b>
                        <b v-else class="text-danger">-{{operation.amount}}{{currency}}</b>
                      </span>
                      <small>{{date(operation.date)}}</small>
                    </label>
                  </b-list-group-item>
                </b-form-checkbox-group>
              </b-list-group>
            </b-card>
          </b-card-group>
        </b-col>
      </b-row>
      <div class="d-flex justify-content-center mt-5">
        <f7-button class="milkyBlockBlack" @click="rapprocher()">
          <span>Rapprocher les opérations</span>
          <b-spinner v-if="buttonLoad" small variant="light"></b-spinner>
        </f7-button>
      </div>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import { currencySymbol } from '@/helpers/labels.js'
import moment from 'moment'

export default {
  name: 'RapprochementManuel',
  components: {
    'page-outline': PageOutline
  },
  data () {
    return {
      rawData: undefined,
      error: false,
      storedError: undefined,
      dpList: [],
      rtpList: [],
      opList: [],
      selectedAccount: '',
      buttonLoad: false
    }
  },
  mounted () {
    this.fetchData()
  },
  computed: {
    dpAll () {
      if (!this.rawData) {
        return []
      } else {
        return this.rawData.reduce((acc, account) => { return [...acc, ...account.needReconciliation.directPayments] }, [])
      }
    },
    rtpAll () {
      if (!this.rawData) {
        return []
      } else {
        return this.rawData.reduce((acc, account) => { return [...acc, ...account.needReconciliation.payments] }, [])
      }
    },
    opAll () {
      if (!this.rawData) {
        return []
      } else if (this.rawData.filter(account => account.iban === this.selectedAccount).length === 0) {
        return []
      } else {
        return this.rawData.filter(account => account.iban === this.selectedAccount)[0].needReconciliation.operations
      }
    },
    accounts () {
      if (!this.rawData) {
        return [{value: '', text: 'Pas de compte bancaire'}]
      } else {
        const accounts = this.rawData
        .sort((a1, a2) => a2.needReconciliation.operations.length - a1.needReconciliation.operations.length)
        .map(account => {
          return {
            value: account.iban,
            text: `${account.name} - ...${account.iban.slice(-4)} - ${account.needReconciliation.operations.length} opérations`
          }
        })
        if (!this.selectedAccount) {
          this.selectedAccount = accounts[0].value
        }
        return accounts
      }
    },
    currency () {
      if (this.rawData && this.rawData.filter(account => account.iban === this.selectedAccount).length > 0) {
        return currencySymbol(this.rawData.filter(account => account.iban === this.selectedAccount)[0].zoneCode)
      } else {
        return ''
      }
    }
  },
  methods: {
    getData () {
      this.rawData = [...this.$engine.bankAccounts.get()]
    },
    fetchData () {
      this.rawData = undefined
      this.error = false
      this.$engine.bankAccounts.getParams()
        .then(response => this.getData())
        .catch(error => {
          this.storedError = error
          this.error = true
        })
    },
    rapprocher () {
      if (!this.buttonLoad) {
        this.buttonLoad = true
        this.$engine.bankAccounts.rapprochementAutomatique(this.opList, this.dpList, this.rtpList)
        .then(response => {
          if (response.error) {
            console.error(response.error)
          } else {
            this.rawData = [...this.$engine.bankAccounts.get()]
          }
          this.buttonLoad = false
          this.$bus.emit('refresh-bank-accounts')
        })
      }
    },
    date (date) {
      return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
    }
  }
}
</script>



// WEBPACK FOOTER //
// RapprochementManuel.vue?e0c5dabc